import { ConnectionItem, Flex, Text } from "@wayflyer/flyui";
import type { BankConnectionSchema, LogoLoadingState } from "bankconnect";
import { useIntl } from "react-intl";

import { BankLogoDisplay } from "../../../../components";
import { MESSAGES } from "../message-definitions";
import type { SelectedAccount } from "../utils";
import { getAccountLabel, getConnectedSubtitle } from "../utils";

type BankAccountsListProps = {
  bankingConnections: BankConnectionSchema[];
  logoLoadingStates: LogoLoadingState;
  onSelectAccount: (selectedAccount: SelectedAccount) => void;
};

export const BankAccountsList = ({ bankingConnections, logoLoadingStates, onSelectAccount }: BankAccountsListProps) => {
  const intl = useIntl();

  const accountItems = bankingConnections.flatMap((connection) =>
    connection.accounts
      .filter((account) => account.pull_enabled)
      .map((account) => (
        <ConnectionItem
          key={account.id}
          label={getAccountLabel(intl, connection, account)}
          logo={<BankLogoDisplay logoState={logoLoadingStates[connection.institutionID]} />}
          subtitle={getConnectedSubtitle(intl, connection, account)}
          badgeStatus="positive"
          badgeText={intl.formatMessage(MESSAGES.readyBadge)}
          actions={[
            {
              content: intl.formatMessage(MESSAGES.viewAccountAction),
              onAction: () => onSelectAccount({ connection, account }),
            },
          ]}
        />
      ))
  );

  if (accountItems.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="2" maxWidth="594px">
      <Flex gap="2">
        <Text fontWeight="semiBold">{intl.formatMessage(MESSAGES.bankAccountsSectionTitle)}</Text>
        <Text color="subtle">{accountItems.length}</Text>
      </Flex>
      <Flex direction="column" gap="2">
        {accountItems}
      </Flex>
    </Flex>
  );
};
